import React, { useState } from 'react';
import { baseurl } from '../baseurl';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const SaveModal = ({ onClose, data, isEdit, projectId, editData }) => {
    const [loading, setLoading] = useState(false);
    const [submissionStatus, setSubmissionStatus] = useState('idle'); // 'idle' | 'success' | 'error'
    const [errorMessage, setErrorMessage] = useState('');
    const navigate = useNavigate();

    const getFileIcon = (type) => {
        if (type?.includes('pdf')) return 'fa-file-pdf text-danger';
        if (type?.includes('image')) return 'fa-file-image text-success';
        return 'fa-file-alt text-primary';
    };


    console.log("editData", editData);
    console.log("projectId", projectId);
    console.log("isEdit", isEdit);

    const handleConfirm = async () => {
        const userToken = localStorage.getItem('userToken');

        if (!userToken) {
            setSubmissionStatus('error');
            setErrorMessage('User session expired. Please login again.');
            return;
        }

        setLoading(true);
        setSubmissionStatus('idle');

        try {
            const formData = new FormData();

            // 1. Append simple fields
            formData.append('propertyId', data.propertyId);
            formData.append('address', data.address);
            formData.append('lotArea', data.lotArea);
            formData.append('status', data.status);
            formData.append('reportType', data.reportType);
            formData.append('totalCredits', data.totalCredits);
            formData.append('estimatedTime', data.estimatedTime);

            // 2. Append stringified objects/arrays
            formData.append('coords', JSON.stringify(data.coords));
            formData.append('geometry', JSON.stringify(data.geometry));
            formData.append('hoaSpecifications', JSON.stringify(data.hoaSpecifications));
            formData.append('instructions', JSON.stringify(data.instructions));

            // 3. Append File (if a new one was uploaded)
            if (data.sitemapFile instanceof File) {
                formData.append('sitemap', data.sitemapFile);
            }

            // 4. Determine Endpoint and Method
            const url = isEdit 
                ? `${baseurl}updateProject/${projectId}` 
                : `${baseurl}saveProject`;
            
            const method = isEdit ? 'put' : 'post';

            const response = await axios({
                method: method,
                url: url,
                data: formData,
                headers: {
                    'Authorization': `Bearer ${userToken}`,
                    'Content-Type': 'multipart/form-data'
                }
            });

            if (response.status === 200 || response.status === 201) {
                setSubmissionStatus('success');
            }
        } catch (error) {
            console.error(`${isEdit ? 'Update' : 'Submission'} Error:`, error);
            setSubmissionStatus('error');
            setErrorMessage(
                error.response?.data?.message || 
                `Failed to ${isEdit ? 'update' : 'generate'} report. Please try again.`
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="summary-overlay">
            <div className="summary-container">
                {/* Header */}
                <div className="summary-header-blue">
                    <div className="header-left">
                        <div className="header-title">
                            <i className={`fas ${isEdit ? 'fa-edit' : 'fa-clipboard-check'} me-2`}></i>
                            {isEdit ? 'Update Parcel Details' : 'Project Summary & Verification Save'}
                        </div>
                        <p className="header-subtitle">
                            {isEdit ? 'Review changes before updating' : 'Please review all details before final submission'}
                        </p>
                    </div>
                    <div className="header-id-badge">{data.propertyId}</div>
                    <button className="summary-close" onClick={onClose} disabled={loading}>&times;</button>
                </div>

                {/* Body (Scrollable) */}
                <div className="summary-body">
                    {/* Property Details */}
                    <section className="summary-section">
                        <h5 className="section-title">
                            <i className="fas fa-info-circle text-primary me-2"></i> Property Details
                        </h5>
                        <div className="summary-grid">
                            <div className="summary-info-box">
                                <label>PROPERTY ADDRESS</label>
                                <div className="info-value">{data.address}</div>
                            </div>
                            <div className="summary-info-box">
                                <label>LOT AREA</label>
                                <div className="info-value"> 


                                     {data.lotArea} sq ft / {(parseFloat(String(data.lotArea).replace(/,/g, '')) / 43560).toFixed(2)} acres


                                </div>
                            </div>
                            <div className="summary-info-box">
                                <label>COORDINATES</label>
                                <div className="info-value">
                                    {data.coords?.[0]?.toFixed(4)}, {data.coords?.[1]?.toFixed(4)}
                                </div>
                            </div>
                            <div className="summary-info-box">
                                <label>STATUS</label>
                                <div className="info-value"><span className="badge-draft">{data.status}</span></div>
                            </div>
                        </div>
                    </section>

                    {/* Configuration & Files Row */}
                    <div className="summary-two-column mt-4">
                        <section className="summary-section flex-1">
                            <h5 className="section-title">Configuration</h5>
                            <div className="type-purple mb-3">{data.reportType}</div>
                            <div className="hoa-summary-list">
                                {Object.entries(data.hoaSpecifications).map(([key, val]) => (
                                    <div key={key} className={`hoa-item ${val ? 'active' : 'inactive'}`}>
                                        <i className={`fas ${val ? 'fa-check-circle' : 'fa-times-circle'}`}></i>
                                        {key.replace(/([A-Z])/g, ' $1').trim()}
                                    </div>
                                ))}
                            </div>
                        </section>

                        <section className="summary-section flex-1">
                            <h5 className="section-title">Attached Sitemap</h5>
                            {data.sitemapFile ? (
                                <div className="sitemap-file-card">
                                    <i className={`fas ${getFileIcon(data.sitemapFile.type)} fa-2x`}></i>
                                    <div className="ms-3">
                                        <div className="file-name">{data.sitemapFile.name}</div>
                                        <div className="file-size">{(data.sitemapFile.size / 1024).toFixed(2)} KB</div>
                                    </div>
                                </div>
                            ) : <div className="no-data-msg">No sitemap provided</div>}
                        </section>
                    </div>

                    {/* Instructions */}
                    <section className="summary-section mt-4">
                        <h5 className="section-title">Special Instructions</h5>
                        <div className="summary-instructions-list">
                            {data.instructions.map((inst, i) => (
                                <div key={i} className="summary-inst-item">
                                    <i className="fas fa-caret-right text-primary me-2"></i> {inst}
                                </div>
                            ))}
                        </div>
                    </section>

                    {/* Pricing */}
                    <section className="summary-section mt-4">
                        <h5 className="section-title">Estimates</h5>
                        <div className="pricing-box">
                            <div className="price-row">
                                <span>Estimated Delivery Time</span>
                                <span className="fw-bold">{data.estimatedTime}</span>
                            </div>
                            <div className="price-row final-total">
                                <span>Final Estimate</span>
                                <span className="total-amount">{data.totalCredits} Credits</span>
                            </div>
                        </div>
                    </section>
                </div>

                {/* Footer Actions - Status Aware */}
                <div className="summary-footer">
                    {submissionStatus === 'idle' && (
                        <>
                            <button className="btn-back" onClick={onClose} disabled={loading}>
                                Back to Edit
                            </button>
                            <button className="btn-confirm" onClick={handleConfirm} disabled={loading}>
                                {loading ? (
                                    <><i className="fas fa-spinner fa-spin me-2"></i> Processing...</>
                                ) : (
                                    <><i className="fas fa-check-circle me-2"></i> {isEdit ? 'Update Parcel' : 'Confirm & Save'}</>
                                )}
                            </button>
                        </>
                    )}

                    {submissionStatus === 'success' && (
                        <div className="footer-status-container">
                            <div className="status-message success">
                                <i className="fas fa-check-circle"></i>
                                <span>{isEdit ? 'Parcel updated successfully!' : 'Project saved as draft. This request has not been submitted yet.'}</span>
                            </div>
                            <button className="btn-confirm" onClick={() => navigate('/projects')}>
                                Go to Reports <i className="fas fa-arrow-right ms-2"></i>
                            </button>
                        </div>
                    )}

                    {submissionStatus === 'error' && (
                        <div className="footer-status-container">
                            <div className="status-message error">
                                <i className="fas fa-exclamation-triangle"></i>
                                <span>{errorMessage}</span>
                            </div>
                            <button className="btn-back" onClick={() => setSubmissionStatus('idle')}>
                                Try Again
                            </button>
                        </div>
                    )}
                </div>
            </div>

            <style> 
                {`
                .summary-footer {
                    display: flex;
                    justify-content: flex-end;
                    gap: 15px;
                    padding: 20px 30px;
                    border-top: 1px solid #edf2f7;
                    background: #f8fafc;
                }

                .footer-status-container {
                    display: flex;
                    width: 100%;
                    justify-content: space-between;
                    align-items: center;
                }

                .status-message {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    font-weight: 500;
                }

                .status-message.success {
                    color: #059669;
                }

                .status-message.error {
                    color: #dc2626;
                }

                .status-message i {
                    font-size: 1.2rem;
                }
                `}
            </style>
        </div>
    );
};

export default SaveModal;